import { AvatarSize, Route } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { AppCountersContext } from 'app/providers/AppCountersProvider';
import unknownUserIcon from 'assets/images/avatar_placeholder.png';
import React, { FC, useCallback, useContext, useMemo, MouseEvent } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Box, useTheme } from '@mui/material';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useStyles } from './UserAvatar.styles';
import { getUserAvatarUrl } from 'shared/utils/user-avatar';

interface UserAvatarProps {
  user?: Partial<UserEntity> | null;
  withOnlineIndicator?: boolean;
  size?: AvatarSize;
  onClick?: () => void;
}

export const UserAvatar: FC<UserAvatarProps> = ({
  user,
  withOnlineIndicator = true,
  size = AvatarSize.XSmall,
  onClick,
}) => {
  const navigate = useNavigate();

  const sizeInSpacing = useMemo(() => {
    switch (size) {
      case AvatarSize.Large:
        return 20;
      case AvatarSize.Medium:
        return 5;
      default:
        return 4;
    }
  }, [size]);

  const theme = useTheme();

  const originalUrl = useMemo(() => {
    const sizeInPixels = parseFloat(theme.spacing(sizeInSpacing));
    return user ? getUserAvatarUrl(user, sizeInPixels) : '';
  }, [user, sizeInSpacing, theme]);

  const { classes } = useStyles({
    sizeInSpacing,
  });

  const isUnknownUser = useMemo(
    () => (!user?.image && !user?.avatarKey) || !user?.userId,
    [user?.image, user?.avatarKey, user?.userId]
  );

  const { activeUsers } = useContext(AppCountersContext);

  const hasOnClick = typeof onClick === 'function';

  const onClickHandler = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!user) {
        return;
      }

      if (hasOnClick) {
        onClick();
        return;
      }

      event.stopPropagation();

      if (user?.id) {
        navigate(`${Route.Players}/${user.id}`);
      }
    },
    [navigate, hasOnClick, user, onClick]
  );

  return (
    <Box className={classNames(classes.root, classes.withHover)}>
      {isUnknownUser ? (
        <img src={unknownUserIcon} />
      ) : (
        <>
          <Box className={classes.avatar} onClick={onClickHandler}>
            <LazyLoadImage
              src={originalUrl}
              placeholderSrc={unknownUserIcon}
              width="100%"
              height="100%"
            />
          </Box>
          {withOnlineIndicator && (
            <Box
              className={classNames(classes.status, {
                online: user?.userId ? activeUsers?.includes(user?.userId) : false,
              })}
            />
          )}
        </>
      )}
    </Box>
  );
};
